import { FormatterService } from "@/domain";
import {
  AppointmentRescheduleStatus,
  AppointmentStatus,
  AppointmentStatuses,
  AppointmentTypes,
  FileBaseUrl,
} from "@/domain/contracts";
import {
  CreatePatientInput,
  CreatePsiInput,
  CreateUserInput,
} from "@/domain/dtos/inputs";
import moment from "moment";
import {
  PatientHomeInfo,
  PatientHomeInfoAppointment,
  PatientHomeInfoVideo,
  PlanSlugs,
  UserApproach,
  UserHomeInfo,
  UserHomeInfoAppointment,
  UserPatient,
  UserPsi,
} from "../../domain/entities";
import { UserMapper } from "../../domain/mappers";
import { CreateUserRequest } from "../dtos/requests";
import {
  AppointmentResponse,
  UserHomeInfoResponse,
  UserResponse,
} from "../dtos/responses";

function calcProfileImprovement(obj: any) {
  if (!obj) return { percentage: 0, trueCount: 0, totalCount: 0 };
  const values = Object.values(obj);
  const trueCount = values.filter((v) => v === true).length;
  const totalCount = values.length;
  return { percentage: (trueCount / totalCount) * 100, trueCount, totalCount };
}

export class UserMapperImpl implements UserMapper<
  UserResponse,
  UserHomeInfoResponse,
  CreateUserRequest
> {
  constructor(private readonly phoneFormatter?: FormatterService) {}
  toDomain(data: UserResponse): UserPsi {
    let connect_account: any = data?.connect_account || null;
    let connect_account_currently_due: string[] = [];
    if (connect_account) {
      connect_account.legal_name =
        connect_account.business_type == "individual"
          ? `${connect_account.individual_first_name} ${connect_account.individual_last_name}`
          : connect_account.company_name;
      connect_account.business_type_name =
        connect_account.business_type == "individual"
          ? "Pessoa Física"
          : "Pessoa Jurídica";

      if (connect_account.currently_due) {
        connect_account.currently_due.split("|").map((item: string) => {
          if (item === "external_account") return;
          connect_account_currently_due.push(item);
        });
      }
    }

    const formatted_profile_image = data.profile_image
      ? { uri: `${FileBaseUrl}${data.profile_image}` }
      : null;

    const bank_account =
      data.bank_account &&
      Array.isArray(data.bank_account) &&
      data.bank_account.length > 0
        ? data.bank_account[0]
        : null;

    const profile_improvement_values = calcProfileImprovement(
      data.profile_improvement,
    );

    let is_subscriber = false;
    let is_plus = false;
    let can_login = false;

    if (data.subscription) {
      const status = data.subscription?.status;
      const trialEnd = data.subscription?.trial_end;
      const cancelAt = data.subscription?.cancel_at;
      const slug = data.subscription?.plan.slug;

      const isTrialingCanceledStillValid =
        status === "canceled" && trialEnd && moment(trialEnd).isAfter(moment());

      const isScheduledToCancel =
        cancelAt && moment(cancelAt).isAfter(moment());

      is_subscriber = !!(
        status === "active" ||
        status === "trialing" ||
        isTrialingCanceledStillValid ||
        isScheduledToCancel
      );

      is_plus = !!(
        is_subscriber &&
        (slug === PlanSlugs.plus || slug === PlanSlugs.free)
      );

      can_login = !!(data.approved && data.stripe_id && is_subscriber);
    }

    const on_holding = !data.approved || !data.stripe_id || !is_subscriber;

    const foreigner = data.foreigner || data.country_code !== "+55";

    const can_have_first_appointment =
      data.first_appointment && data.appointments_with_this_psy_count === 0;

    let partnership = null;
    if (data.partnership && data.partnership.partnership_code) {
      partnership = {
        id: data.partnership.id,
        info: {
          code: data.partnership.partnership_code.code,
          company_name: data.partnership.info.company_name,
        },
      };
    }

    const isPremium = data.valid_psiapp_plus_subscription_count > 0;

    const formatted_rating = (data.avg_rating || 0).toFixed(1);
    const formatted_exp = `${data.years_exp} ${data.years_exp > 1 ? "anos" : "ano"} de experiência`;
    const formatted_ratings_as_psi_count = `${data.ratings_as_psi_count} ${data.ratings_as_psi_count === 1 ? "depoimento" : "depoimentos"}`;
    const formatted_valid_appointments_as_psi_count = `${data.valid_appointments_as_psi_count} ${data.valid_appointments_as_psi_count === 1 ? "sessão" : "sessões"}`;
    const formatted_languages = (data.languages ?? []).map(
      (language: any) => language.detail.name,
    );

    let formatted_approaches: UserApproach[] = [];
    if (data.approaches) {
      formatted_approaches = data.approaches.filter(
        (r: UserApproach) => r.info != null,
      );
    }
    // const formatted_profile_image = `${FileBaseUrl}${data.profile_image}`;

    let formatted_intro_video_link = "";
    if (data.intro_video_link) {
      formatted_intro_video_link = `https://www.youtube.com/embed/${data.intro_video_link}`;
    }
    let formatted_latest_ratings = [];
    if (data.latest_ratings?.length) {
      formatted_latest_ratings = data.latest_ratings
        .filter((elem: any) => elem.user != null)
        .map((elem: any) => {
          return {
            ...elem,
            img: elem.user.profile_image || 1,
            initials: `${elem.user.name.charAt(0)}.`,
          };
        });
    }

    return {
      id: data.id,
      name: data.name,
      nickname: data.nickname,
      email: data.email,
      phone: data.phone,
      external_schedule_phone: data.external_schedule_phone ?? null,
      country_code: data.country_code,
      profile_image: data.profile_image,
      formatted_profile_image,
      document_number: data.document_number,
      document_image: data.document_image,
      document_in_hand: data.document_in_hand,
      slug: data.slug,
      type: data.type,
      gender: data.gender,
      accepted_gender:
        data.accepted_gender && data.accepted_gender !== "all"
          ? data.accepted_gender
          : null,
      birth_date: data.birth_date,
      foreigner,
      online: data.online,
      active: data.active,
      approved: data.approved,
      deleted_at: data.deleted_at,
      stripe_id: data.stripe_id,
      charges_enabled: data.charges_enabled,
      handicapped: data.handicapped,
      notification_token: data.notification_token,
      crp: data.crp,
      epsi: data.epsi,
      years_exp: data.years_exp,
      bio: data.bio,
      education: data.education,
      linkedin: data.linkedin,
      address_state: data.address_state,
      appointment_price: data.appointment_price,
      first_appointment_price: data.first_appointment_price,
      recurrent_price: data.recurrent_price ?? null,
      first_appointment: data.first_appointment,
      block_agenda: data.block_agenda,
      imediate: data.imediate,
      partnerships: data.partnerships,
      avg_rating: data.avg_rating,
      intro_video_link: data.intro_video_link,
      unread_chat_messages: data.unread_chat_messages,
      total_views: data.total_views,
      total_views_this_month: data.total_views_this_month,
      ratings_as_psi_count: data.ratings_as_psi_count,
      valid_appointments_as_psi_count: data.valid_appointments_as_psi_count,
      valid_psiapp_plus_subscription_count:
        data.valid_psiapp_plus_subscription_count,
      appointments_with_this_psy_count: data.appointments_with_this_psy_count,
      account_pending_verification: data.account_pending_verification,
      subscription: data.subscription,
      approaches: data.approaches,
      languages: data.languages,
      occupations: data.occupations,
      hours: data.hours,
      latest_ratings: data.latest_ratings,
      profile_improvement: data.profile_improvement ?? {
        minimum_hours: false,
        has_approaches: false,
        first_appointment_active: false,
        introduction_video: false,
        partnerships: false,
        premium_user: false,
      },
      profile_improvement_values,
      bank_account,
      connect_account,
      connect_account_currently_due,
      entry_fee: data.entry_fee,
      account_verification_errors: data.account_verification_errors,
      partnership,
      favorite_card: null,
      can_login,
      on_holding,
      is_subscriber,
      is_plus,
      can_have_first_appointment,
      isPremium,
      formatted_approaches,
      formatted_intro_video_link,
      formatted_exp,
      formatted_latest_ratings,
      formatted_rating,
      formatted_ratings_as_psi_count,
      formatted_valid_appointments_as_psi_count,
      formatted_languages,
      account_link: data.account_link,
    };
  }

  toPatientDomain(data: UserResponse): UserPatient {
    const country_code = data.country_code || "+55";

    let partnership = null;
    if (data.partnership && data.partnership.partnership_code) {
      partnership = {
        id: data.partnership.id,
        info: {
          code: data.partnership.partnership_code.code,
          company_name: data.partnership.info.company_name,
          discount_percentage: data.partnership.info.discount_percentage,
        },
      };
    }

    const dName = data.name.split(" ");
    const display_name = dName[0] || data.nickname;

    const foreigner = data.foreigner || data.country_code !== "+55";

    return {
      id: data.id,
      name: data.name,
      nickname: data.nickname,
      display_name,
      slug: data.slug,
      email: data.email,
      phone: data.phone,
      country_code,
      document_number: data.document_number,
      profile_image: data.profile_image,
      formatted_profile_image: data.profile_image
        ? { uri: `${FileBaseUrl}${data.profile_image}` }
        : null,
      foreigner,
      unread_chat_messages: data.unread_chat_messages,
      favorite_card: null,
      default_payment_method: null,
      partnership,
      handicapped: data.handicapped,
      gender: data.gender,
      deleted_at: data.deleted_at,
    };
  }

  toHomeInfo(data: UserHomeInfoResponse): UserHomeInfo {
    const user = this.toDomain(data.user);
    const recent_psy = data.recent_psy.map((elem) => this.toDomain(elem));
    const videos = data.videos;
    const unread_notifications = data.unread_notifications;

    const agendado = data.agendado.map((elem: any) =>
      this.toAppointmentDomain(elem),
    );

    const aguardando = data.aguardando.map((elem: any) =>
      this.toAppointmentDomain(elem),
    );

    let imediate = null;
    if (data.imediate) {
      imediate = this.toAppointmentDomain(data.imediate);
    }

    let in_progress = null;
    if (data.in_progress) {
      in_progress = this.toAppointmentDomain(data.in_progress);
    }

    return {
      user,
      agendado,
      aguardando,
      imediate,
      in_progress,
      videos,
      recent_psy,
      unread_notifications,
    };
  }

  toPatientHomeInfo(data: UserHomeInfoResponse): PatientHomeInfo {
    const scheduled_appointments: PatientHomeInfoAppointment[] =
      data.agendado.map((elem) => {
        const appointment: PatientHomeInfoAppointment = {
          id: elem.id,
          psychologist: {
            profile_image: elem.psychologist.profile_image,
            name: elem.psychologist.name,
          },
          status: AppointmentStatuses[elem.status],
          date: elem.date,
          has_pending_reschedule: false,
        };

        if (elem.reschedule_requests?.length) {
          const sch = elem.reschedule_requests[0];

          appointment.has_pending_reschedule =
            sch && sch.status === AppointmentRescheduleStatus.pending;

          if (sch.status !== "canceled") {
            appointment.status =
              AppointmentStatuses[sch.status as AppointmentStatus];
          }
        }
        return appointment;
      });

    let on_going_appointment = null;
    if (data.in_progress) {
      const title =
        data.in_progress?.type === AppointmentTypes.imediate
          ? "Sessão imediata"
          : "Sessão agendada";
      let msg = "";
      let img = null;

      if (data.in_progress?.status === AppointmentStatuses["em andamento"]) {
        msg = "Sessão em andamento";
        img = {
          uri: `${FileBaseUrl}${data.in_progress.psychologist.profile_image}`,
        };
      } else if (
        data.in_progress?.type === AppointmentTypes.imediate &&
        data.in_progress?.status === "pendente"
      ) {
        msg = "Buscando sessão com psi";
      }

      on_going_appointment = {
        id: data.in_progress.id,
        status: data.in_progress.status,
        title,
        msg,
        img,
      };
    }

    const my_psis =
      data.recent_psy
        ?.map((r: any) => r.psychologist)
        .filter((p) => !p.deleted_at)
        .map((e: UserResponse) => this.toDomain(e)) || [];

    const videos: PatientHomeInfoVideo[] = data.videos.map((elem: any) => {
      return {
        ...elem,
        video: { uri: `${FileBaseUrl}${elem.video_path}` },
        image: {
          uri: `${FileBaseUrl}${elem.thumbnail_path}`,
        },
        name: elem.description,
      };
    });

    return {
      unread_notifications: data.unread_notifications,
      on_going_appointment,
      scheduled_appointments,
      my_psis,
      videos,
    };
  }

  // depreciada
  toCreateRequest(data: CreateUserInput): CreateUserRequest {
    if (this.phoneFormatter?.reverse) {
      const formatted = this.phoneFormatter.reverse(data.phone);
      data.phone = formatted as string;
    }
    return data;
  }

  toCreatePatient(data: CreatePatientInput): CreateUserRequest {
    if (this.phoneFormatter?.reverse) {
      const formatted = this.phoneFormatter.reverse(data.phone);
      data.phone = formatted as string;
    }
    return data;
  }

  toCreatePsi(data: CreatePsiInput): CreateUserRequest {
    if (this.phoneFormatter?.reverse) {
      data.phone = this.phoneFormatter.reverse(data.phone) as string;
    }

    return {
      ...data,
      account_link: true,
      web: true,
      document_number: "",
      document_valid: true,
    };
  }

  private toAppointmentDomain(
    item: AppointmentResponse,
  ): UserHomeInfoAppointment {
    const rescheduleRequest = item?.reschedule_requests[0];

    let statusLabel =
      item?.status === AppointmentStatuses.aguardando
        ? "Aguardando confirmação"
        : "Sessão confirmada";

    let hasPendingReschedule =
      rescheduleRequest &&
      rescheduleRequest.status == AppointmentRescheduleStatus.pending;

    if (hasPendingReschedule) {
      statusLabel = "Reagendamento Pendente";
    }

    const date = `Dia ${moment(item?.date).format("DD | dddd")}`;
    const time = `das ${moment(item?.date).format("HH:mm")} às ${moment(
      item?.date,
    )
      .add(50, "minutes")
      .format("HH:mm")}`;

    const card_time = `Sessão das ${moment(item?.date).format(
      "HH[h]mm",
    )} às ${moment(item?.date).add(50, "minutes").format("HH[h]mm")}`;

    const card_button_label =
      item?.status === "em andamento" ? "Entrar na sala" : "Ver solicitação";

    return {
      id: item.id,
      user: {
        name: item.user.name,
        profile_image: item.user.profile_image,
      },
      date,
      time,
      status: {
        value: item.status,
        label: statusLabel,
      },
      has_pending_reschedule: hasPendingReschedule,
      can_enter: item.can_enter,
      original_date: item.date,
      call_link: item.call_link,
      call_provider: item.call_provider,
      card_time,
      card_button_label,
    };
  }
}
